import React, { useState, useEffect } from "react";
import { Search, FileText, ArrowUpDown, Truck } from "lucide-react";
import { getInvoices } from "../../services/invoiceService";
import InvoiceView from "./InvoiceView";
import TrackingView from "./TrackingView";
import { checkAuth } from "../../utils/auth";

const formatCurrency = (value) => {
  if (!value) return "R$ 0,00";
  const numValue = Number(value);
  return numValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedChaveNfe, setSelectedChaveNfe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const fetchInvoices = async () => {
    try {
      if (!checkAuth()) {
        window.location.href = "/login";
        return;
      }
      setLoading(true);
      const data = await getInvoices();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Erro ao carregar notas fiscais");
      console.error("Erro:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const SortableHeader = ({ label, field }) => (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center">
        {label}
        <ArrowUpDown
          className={`w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 ${
            sortConfig.key === field ? "opacity-100" : ""
          }`}
        />
      </div>
    </th>
  );

  const sortedInvoices = [...invoices].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    if (sortConfig.key === "DATA_EMISSAO") {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (sortConfig.key === "VALOR_TOTAL_NOTA_FISCAL") {
      aValue = Number(aValue);
      bValue = Number(bValue);
    }
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredInvoices = sortedInvoices.filter(
    (invoice) =>
      invoice.NUMERO?.trim().toLowerCase().includes(search.toLowerCase()) ||
      invoice.CLIENTE_CODIGO?.toLowerCase().includes(search.toLowerCase())||
      invoice.CLIENTE_NOME?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p>{error}</p>
          <button
            onClick={fetchInvoices}
            className="mt-2 bg-red-100 px-4 py-2 rounded hover:bg-red-200 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header com Sticky e Filtros */}
      <div className="sticky top-0 bg-gray-50 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl font-semibold text-gray-900">
              Notas Fiscais
            </h1>
          </div>
          <div className="mt-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por número ou cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-shadow bg-white"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <SortableHeader label="Data Emissão" field="DATA_EMISSAO" />
                <SortableHeader label="Número" field="NUMERO" />
                <SortableHeader label="Cliente" field="CLIENTE_CODIGO" />
                <SortableHeader
                  label="Valor Total"
                  field="VALOR_TOTAL_NOTA_FISCAL"
                />
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    Nenhuma nota fiscal encontrada
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => (
                  <tr key={invoice.NUMERO} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(invoice.DATA_EMISSAO).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.NUMERO.trim()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.CLIENTE_CODIGO} - {invoice.CLIENTE_NOME}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(invoice.VALOR_TOTAL_NOTA_FISCAL)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() =>
                            setSelectedInvoiceId(invoice.NUMERO.trim())
                          }
                          className="text-blue-600 hover:text-blue-900"
                          title="Ver detalhes"
                        >
                          <FileText className="w-5 h-5" />
                        </button>
                        {invoice.CHAVE_NFE && (
                          <button
                            onClick={() =>
                              setSelectedChaveNfe(invoice.CHAVE_NFE.trim())
                            }
                            className="text-green-600 hover:text-green-900"
                            title="Rastrear entrega"
                          >
                            <Truck className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedInvoiceId && (
        <InvoiceView
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
        />
      )}

      {selectedChaveNfe && (
        <TrackingView
          chaveNfe={selectedChaveNfe}
          onClose={() => setSelectedChaveNfe(null)}
        />
      )}
    </div>
  );
}

export default InvoiceList;
